import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import {
  ArrowLeft, Users, Search, Download, Share2, QrCode, Image as ImageIcon,
  TrendingUp, Megaphone, Award,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";

const PLATFORM_COLORS = {
  whatsapp: "#25D366",
  x: "#0F172A",
  facebook: "#1877F2",
  linkedin: "#0A66C2",
  email: "#475569",
  copy: "#5B4CFF",
};
const PLATFORM_LABELS = {
  whatsapp: "WhatsApp",
  x: "X",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  email: "Email",
  copy: "Copy link",
};

function Stat({ icon: Icon, label, value, sub, accent = false, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono tracking-widest text-[#475569]">{label.toUpperCase()}</p>
        <Icon size={14} className={accent ? "text-[#5B4CFF]" : "text-[#475569]"} />
      </div>
      <p className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-[#0F172A] tracking-tighter">
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      {sub && <p className="mt-1 text-xs text-[#475569]">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children, hint, icon: Icon }) {
  return (
    <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="kicker mb-1 inline-flex items-center gap-2">
          {Icon && <Icon size={12} />} {children}
        </p>
      </div>
      {hint && <p className="text-xs text-[#475569] max-w-md">{hint}</p>}
    </div>
  );
}

export default function AdminEventAnalytics() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/admin/events/${id}/analytics`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load analytics"));
  }, [id]);

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-10 py-8 md:py-12 max-w-7xl">
        <p className="text-sm text-[#EF4444]" data-testid="analytics-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="px-4 sm:px-6 lg:px-10 py-8 md:py-12 max-w-7xl">
        <p className="font-mono text-sm text-[#475569]">LOADING…</p>
      </div>
    );
  }

  const ev = data.event;
  const reach = data.reach;
  const eng = data.engagement;
  const share = data.sharing;
  const roi = data.sponsor_roi;
  const trends = data.trends.series_14d;
  const platformData = share.by_platform.map((p) => ({
    name: PLATFORM_LABELS[p.target] || p.target,
    value: p.count,
    fill: PLATFORM_COLORS[p.target] || "#5B4CFF",
    pct: p.pct,
  }));

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 md:py-12 max-w-7xl" data-testid="event-analytics-page">
      <Link to={`/admin/events/${id}`} className="text-xs text-[#475569] hover:text-[#0F172A] inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={12} /> BACK TO EVENT
      </Link>

      <div className="mb-8">
        <p className="kicker mb-2">EVENT ANALYTICS · {ev.status.toUpperCase()}</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tighter" data-testid="analytics-event-name">
          {ev.name}
        </h1>
        <p className="mt-2 text-sm text-[#475569]">
          {ev.city}, {ev.country} · {ev.event_date} · Live metrics for ROI proof.
        </p>
      </div>

      {/* SPONSOR ROI HEADLINE */}
      <div className={`mb-8 border p-6 md:p-8 ${roi.has_strip ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-[#F8FAFC] border-[#E2E8F0]"}`}
        data-testid="sponsor-roi-panel">
        <div className="flex items-start gap-4 mb-6">
          <div className={`w-10 h-10 flex items-center justify-center shrink-0 ${roi.has_strip ? "bg-[#5B4CFF]" : "bg-white border border-[#E2E8F0]"}`}>
            <Megaphone size={20} className={roi.has_strip ? "text-white" : "text-[#475569]"} />
          </div>
          <div className="min-w-0">
            <p className={`text-[10px] font-mono tracking-widest ${roi.has_strip ? "text-white/70" : "text-[#475569]"}`}>SPONSOR ROI</p>
            <h2 className="font-display text-xl md:text-2xl font-bold">
              {roi.has_strip ? "Advert strip live — sponsor value building" : "No advert strip uploaded yet"}
            </h2>
            {!roi.has_strip && (
              <p className={`mt-2 text-sm ${roi.has_strip ? "text-white/80" : "text-[#475569]"}`}>
                Upload a sponsor strip in Cover, Advert &amp; QR to start compounding sponsor impressions on every download and share.
              </p>
            )}
          </div>
        </div>
        {roi.has_strip && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] font-mono tracking-widest text-white/60">IMPRESSIONS</p>
              <p className="font-display text-3xl md:text-4xl font-extrabold" data-testid="roi-impressions">
                {roi.sponsor_impressions.toLocaleString()}
              </p>
              <p className="text-xs text-white/60 mt-1">Downloads + shares</p>
            </div>
            <div>
              <p className="text-[10px] font-mono tracking-widest text-white/60">DOWNLOADS W/ STRIP</p>
              <p className="font-display text-3xl md:text-4xl font-extrabold">{roi.sponsor_downloads.toLocaleString()}</p>
              <p className="text-xs text-white/60 mt-1">Full-res, saved by runners</p>
            </div>
            <div>
              <p className="text-[10px] font-mono tracking-widest text-white/60">SHARES W/ STRIP</p>
              <p className="font-display text-3xl md:text-4xl font-extrabold">{roi.sponsor_shares.toLocaleString()}</p>
              <p className="text-xs text-white/60 mt-1">Amplified on social</p>
            </div>
            <div>
              <p className="text-[10px] font-mono tracking-widest text-white/60">EST. REACH</p>
              <p className="font-display text-3xl md:text-4xl font-extrabold text-[#10B981]">{roi.estimated_reach.toLocaleString()}</p>
              <p className="text-xs text-white/60 mt-1">Downloads + shares × 5</p>
            </div>
          </div>
        )}
      </div>

      {/* REACH */}
      <SectionTitle icon={Users}>REACH · WHO FOUND YOU</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Search} label="Bib searches" value={reach.total_searches} sub={`${reach.searches_24h} in last 24 h`} accent testId="stat-searches" />
        <Stat icon={Users} label="Unique bibs" value={reach.unique_bibs_searched} sub="Different runners searched" accent testId="stat-unique-bibs" />
        <Stat icon={QrCode} label="QR scans" value={reach.qr_scans} sub="From printed QR at event" testId="stat-qr-scans" />
        <Stat icon={ImageIcon} label="Photos published" value={eng.photos_uploaded} sub={`${eng.avg_photos_per_bib} avg per bib`} testId="stat-photos" />
      </div>

      {/* ENGAGEMENT */}
      <SectionTitle icon={Download}>ENGAGEMENT · WHAT THEY DID</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Download} label="Total downloads" value={eng.total_downloads} sub={`${eng.downloads_24h} in last 24 h`} accent testId="stat-downloads" />
        <Stat icon={Users} label="Bibs w/ photos" value={eng.bibs_with_photos} sub="Runners with at least 1 match" testId="stat-bibs-with-photos" />
        <Stat icon={Share2} label="Total shares" value={share.total_shares} sub={`${share.shares_24h} in last 24 h`} accent testId="stat-shares" />
        <Stat icon={TrendingUp} label="Share rate" value={`${share.share_conversion_rate_pct}%`} sub="Shares ÷ unique bibs" testId="stat-share-rate" />
      </div>

      {/* SHARING BREAKDOWN */}
      <SectionTitle icon={Share2} hint="Every share carries your sponsor strip and event branding into WhatsApp threads, X timelines and Facebook feeds.">SHARING · WHERE IT SPREAD</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-[#E2E8F0] p-6 lg:col-span-2" data-testid="chart-platforms">
          <div className="mb-4 flex items-center justify-between">
            <p className="kicker">BY PLATFORM</p>
            <p className="text-xs text-[#475569] font-mono">TOTAL {share.total_shares}</p>
          </div>
          {share.total_shares === 0 ? (
            <p className="text-sm text-[#475569] py-12 text-center">No shares yet — data will populate as runners share.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={platformData} margin={{ top: 10, right: 0, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 11 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                <YAxis tick={{ fill: "#475569", fontSize: 11 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "#F8FAFC" }} contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 0 }} />
                <Bar dataKey="value" name="Shares" radius={[0, 0, 0, 0]}>
                  {platformData.map((p, i) => <Cell key={i} fill={p.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E2E8F0] p-6" data-testid="chart-share-type">
          <p className="kicker mb-4">TYPE</p>
          {share.total_shares === 0 ? (
            <p className="text-sm text-[#475569]">No shares yet.</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={[
                      { name: "Gallery", value: share.gallery_shares, fill: "#5B4CFF" },
                      { name: "Single photo", value: share.photo_shares, fill: "#0F172A" },
                    ]}
                    dataKey="value"
                    innerRadius={40}
                    outerRadius={70}
                  />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 0 }} />
                  <Legend verticalAlign="bottom" iconType="square" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 flex justify-between text-xs">
                <span className="text-[#475569]">Gallery: <b className="text-[#0F172A]">{share.gallery_shares}</b></span>
                <span className="text-[#475569]">Photo: <b className="text-[#0F172A]">{share.photo_shares}</b></span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* TRENDS */}
      <SectionTitle icon={TrendingUp}>TRENDS · LAST 14 DAYS</SectionTitle>
      <div className="bg-white border border-[#E2E8F0] p-6 mb-8" data-testid="chart-trends">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={trends} margin={{ top: 10, right: 0, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#475569", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
            <YAxis tick={{ fill: "#475569", fontSize: 11 }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 0 }} />
            <Legend iconType="line" wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="searches" name="Searches" stroke="#5B4CFF" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="downloads" name="Downloads" stroke="#10B981" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="shares" name="Shares" stroke="#0F172A" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="qr_scans" name="QR scans" stroke="#F59E0B" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* TOP BIBS */}
      <SectionTitle icon={Award}>TOP BIBS · ADVOCATES &amp; SUPERFANS</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TopBibList title="Most searched" bibs={data.top_bibs.by_searches} unit="searches" testId="top-searched" />
        <TopBibList title="Most downloaded" bibs={data.top_bibs.by_downloads} unit="downloads" testId="top-downloaded" />
      </div>
    </div>
  );
}

function TopBibList({ title, bibs, unit, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] p-6" data-testid={testId}>
      <p className="kicker mb-4">{title.toUpperCase()}</p>
      {bibs.length === 0 ? (
        <p className="text-sm text-[#475569]">No data yet.</p>
      ) : (
        <ul className="space-y-3">
          {bibs.map((b, idx) => (
            <li key={b.bib} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono text-xs text-[#5B4CFF] w-6">{String(idx + 1).padStart(2, "0")}</span>
                <span className="font-mono font-semibold text-[#0F172A]">#{b.bib}</span>
              </div>
              <span className="font-mono text-xs text-[#475569]">{b.count} {unit}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
